/**
 * TERRA Bridge — connects the AI assistant to the Data Vault's Chart API.
 *
 * The Data Vault exposes `window.__ARGOS_CHART_API__` (see useChartAPI.ts).
 * This bridge wraps it so TERRA's SSE action events map cleanly to the
 * existing API without the Data Vault needing any TERRA-specific code.
 */
import type { TerraAction } from '@/api/terra';
import { useDataVaultStore } from '@/store/dataVault';

// Re-export the types from the Chart API for convenience
interface ArgosChartAPI {
  getAvailableCharts: () => { id: string; label: string; category: string }[];
  setActiveChart: (chartType: string) => boolean;
  setFilters: (input: {
    sites?: string[];
    wells?: string[];
    analytes?: string[];
    dateRange?: { from: string; to: string };
  }) => void;
  getChartData: () => unknown;
  getChartInsights: () => {
    count: number;
    min: number | null;
    max: number | null;
    mean: number | null;
    exceedances: number;
    trendDirection: 'up' | 'down' | 'flat' | 'unknown';
  };
}

function getAPI(): ArgosChartAPI | null {
  return (window as any).__ARGOS_CHART_API__ ?? null;
}

// ── Public API ──────────────────────────────────────────────────────────

/**
 * Detect which product/page the user is currently on.
 */
export function readProductContext(): { product: string; page: string; siteId?: string } {
  const path = window.location.pathname;
  // Extract site ID from URL patterns like /polygon/sites/:id or /data-vault/sites/:id
  const siteMatch = path.match(/\/sites\/([0-9a-f-]{36})/);
  const siteId = siteMatch?.[1];

  if (path.startsWith('/polygon')) return { product: 'polygon', page: path, siteId };
  if (path.startsWith('/phreatic')) return { product: 'phreatic', page: path, siteId };
  if (path.startsWith('/data-vault')) return { product: 'data-vault', page: path, siteId };
  if (path.startsWith('/pathfinder')) return { product: 'pathfinder', page: path, siteId };
  if (path.startsWith('/precept')) return { product: 'precept', page: path, siteId };
  if (path.startsWith('/protocol')) return { product: 'protocol', page: path, siteId };
  return { product: 'general', page: path, siteId };
}

/**
 * Read current context for injection into TERRA requests.
 * Combines product context with Data Vault chart insights if available.
 */
export function readDataVaultContext(): Record<string, unknown> | null {
  const productCtx = readProductContext();
  const api = getAPI();

  const context: Record<string, unknown> = {
    product: productCtx.product,
    page: productCtx.page,
    site_id: productCtx.siteId,
  };

  // Include current Data Vault filter state so TERRA knows what's selected
  const dvState = useDataVaultStore.getState();
  if (dvState.selectedSites.length > 0) context.selected_sites = dvState.selectedSites;
  if (dvState.selectedWells.length > 0) context.selected_wells = dvState.selectedWells;
  if (dvState.selectedAnalytes.length > 0) context.selected_analytes = dvState.selectedAnalytes;
  if (dvState.dateFrom) context.date_from = dvState.dateFrom;
  if (dvState.dateTo) context.date_to = dvState.dateTo;

  if (api) {
    try {
      const insights = api.getChartInsights();
      context.active_tab = 'data-vault';
      context.chart_insights = insights;
    } catch {
      context.active_tab = 'data-vault';
    }
  }

  return context;
}

/**
 * Dispatch a TERRA action to the Data Vault UI via window.__ARGOS_CHART_API__.
 * Called by the TERRA store when an SSE action event arrives.
 */
export function dispatchTerraAction(action: TerraAction): void {
  const api = getAPI();

  // If chart action but not on Data Vault, navigate there first then wait for API
  if (!api && (action.type === 'navigate' || action.type === 'filter' || action.type === 'scroll_to_chart')) {
    const chartPath = '/data-vault/charts';
    if (!window.location.pathname.startsWith('/data-vault')) {
      navigateToPage(chartPath);

      // Listen for the chart-api-ready event from useChartAPI; fall back to a
      // short timeout if it never fires (e.g. user navigated away again).
      const TIMEOUT_MS = 5000;
      let done = false;
      const onReady = () => {
        if (done) return;
        done = true;
        window.removeEventListener('argos:chart-api-ready', onReady);
        clearTimeout(timeout);
        dispatchTerraAction(action);
      };
      const timeout = setTimeout(() => {
        if (done) return;
        done = true;
        window.removeEventListener('argos:chart-api-ready', onReady);
        // Last-chance check in case the event fired before we attached.
        if (getAPI()) {
          dispatchTerraAction(action);
        } else {
          console.warn(`TERRA: Chart API did not mount within ${TIMEOUT_MS}ms after navigation`);
        }
      }, TIMEOUT_MS);
      window.addEventListener('argos:chart-api-ready', onReady);
    }
    return;
  }

  if (!api) return;

  switch (action.type) {
    case 'navigate': {
      // Set the active chart type
      if (action.chartType) {
        api.setActiveChart(action.chartType);
      } else if (action.tab) {
        // Map tab names to a default chart type per tab
        const tabDefaults: Record<string, string> = {
          // Main tabs
          analytical: 'area-series',
          timeseries: 'area-series',
          gauging: 'water-levels',
          risk: 'radial-gauge',
          compliance: 'compliance-matrix',
          spatial: 'well-map',
          remediation: 'waterfall',
          // Chart categories
          distribution: 'box-plot',
          comparison: 'ranked-bar',
          composition: 'sankey',
          relationship: 'sankey',
          interactive: 'small-multiples',
          matrix: 'exceedance-heatmap',
          gauges: 'radial-gauge',
          environmental: 'piper',
        };
        const defaultChart = tabDefaults[action.tab];
        if (defaultChart) api.setActiveChart(defaultChart);
      }

      // Apply any filters that came with the navigation
      const filters: Parameters<ArgosChartAPI['setFilters']>[0] = {};
      if (action.siteId) filters.sites = [action.siteId];
      if (action.wellNames) filters.wells = action.wellNames;
      if (action.analyteCodes) filters.analytes = action.analyteCodes;
      if (action.dateFrom || action.dateTo) {
        filters.dateRange = {
          from: action.dateFrom || '',
          to: action.dateTo || '',
        };
      }
      if (Object.keys(filters).length > 0) {
        api.setFilters(filters);
      }
      break;
    }

    case 'filter': {
      const f = action.filters;
      const filters: Parameters<ArgosChartAPI['setFilters']>[0] = {};
      if (f.selectedSites) filters.sites = f.selectedSites as string[];
      if (f.selectedWells) filters.wells = f.selectedWells as string[];
      if (f.selectedAnalytes) filters.analytes = f.selectedAnalytes as string[];
      if (f.dateFrom || f.dateTo) {
        filters.dateRange = {
          from: (f.dateFrom as string) || '',
          to: (f.dateTo as string) || '',
        };
      }
      api.setFilters(filters);
      break;
    }

    case 'highlight': {
      // Highlight is a filter-level action — select the wells
      api.setFilters({ wells: action.wellNames });
      break;
    }

    case 'scroll_to_chart': {
      api.setActiveChart(action.chartId);
      break;
    }

    case 'clear_highlights': {
      // Clear well filter
      api.setFilters({ wells: [] });
      break;
    }
  }
}

/**
 * Navigate to a different page/product in the app.
 * Called directly from the TERRA store when a navigate_page action arrives.
 */
export function navigateToPage(path: string): void {
  // Use history pushState for SPA navigation
  window.history.pushState({}, '', path);
  // Dispatch popstate event so React Router picks it up
  window.dispatchEvent(new PopStateEvent('popstate'));
}
